import React, { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Upload, 
  X, 
  CheckCircle, 
  AlertCircle, 
  RefreshCw,
  File,
  Loader2,
  FolderOpen
} from 'lucide-react';
import { apiService } from '../services/api';
import { FolderStructure } from '../../../shared/types';
import { formatFileSize } from '../utils/formatters';
import { 
  MAX_FILE_SIZE, 
  MULTIPART_THRESHOLD, 
  ALLOWED_AUDIO_TYPES,
  QUERY_KEYS 
} from '../utils/constants';

interface FileUploadProps {
  selectedFolder?: FolderStructure;
  onUploadComplete?: () => void;
  className?: string;
}

interface UploadFile {
  id: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
  uploadUrl?: string;
  fileId?: string;
  abortController?: AbortController;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  selectedFolder,
  onUploadComplete,
  className = ''
}) => {
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const queryClient = useQueryClient();
  const uploadIdCounter = useRef(0);

  // Generate presigned upload URL mutation
  const generateUploadUrlMutation = useMutation({
    mutationFn: apiService.generateUploadUrl.bind(apiService),
  });

  // File validation
  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds maximum limit of ${formatFileSize(MAX_FILE_SIZE)}`;
    }
    
    if (!ALLOWED_AUDIO_TYPES.includes(file.type)) {
      return 'Only audio files are allowed';
    }
    
    return null;
  };

  // Handle file drop/selection
  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    if (!selectedFolder) {
      return;
    }

    // Handle rejected files
    rejectedFiles.forEach(({ file, errors }) => {
      console.warn(`File ${file.name} rejected:`, errors);
    });

    // Process accepted files
    const newUploadFiles: UploadFile[] = acceptedFiles.map(file => {
      const validationError = validateFile(file);
      return {
        id: `upload-${++uploadIdCounter.current}`,
        file,
        progress: 0,
        status: validationError ? 'error' : 'pending',
        error: validationError || undefined,
      };
    });

    setUploadFiles(prev => [...prev, ...newUploadFiles]);
  }, [selectedFolder]);

  // Configure dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ALLOWED_AUDIO_TYPES.map(type => type.replace('audio/', '.'))
    },
    maxSize: MAX_FILE_SIZE,
    multiple: true,
    disabled: !selectedFolder || isUploading
  });

  // Upload a single file
  const uploadSingleFile = async (uploadFile: UploadFile): Promise<void> => {
    if (!selectedFolder) {
      throw new Error('No folder selected');
    }

    try {
      // Update status to uploading
      setUploadFiles(prev => prev.map(f => 
        f.id === uploadFile.id 
          ? { ...f, status: 'uploading', progress: 0 }
          : f
      ));

      // Generate presigned upload URL
      const uploadUrlResponse = await generateUploadUrlMutation.mutateAsync({
        fileName: uploadFile.file.name,
        folderPath: selectedFolder.folderPath,
        contentType: uploadFile.file.type,
        sizeBytes: uploadFile.file.size
      });

      // Create abort controller for this upload
      const abortController = new AbortController();
      
      // Update upload file with URL and abort controller
      setUploadFiles(prev => prev.map(f => 
        f.id === uploadFile.id 
          ? { 
              ...f, 
              uploadUrl: uploadUrlResponse.uploadUrl,
              fileId: uploadUrlResponse.fileId,
              abortController 
            }
          : f
      ));

      // Determine if we need multipart upload
      const useMultipart = uploadFile.file.size > MULTIPART_THRESHOLD;

      if (useMultipart) {
        await uploadMultipart(uploadFile, uploadUrlResponse.uploadUrl, abortController);
      } else {
        await uploadDirect(uploadFile, uploadUrlResponse.uploadUrl, abortController);
      }

      // Mark as completed
      setUploadFiles(prev => prev.map(f => 
        f.id === uploadFile.id 
          ? { ...f, status: 'completed', progress: 100 }
          : f
      ));

    } catch (error: any) {
      // Handle upload error
      const errorMessage = error.name === 'AbortError' 
        ? 'Upload cancelled' 
        : error.message || 'Upload failed';
        
      setUploadFiles(prev => prev.map(f => 
        f.id === uploadFile.id 
          ? { ...f, status: 'error', error: errorMessage }
          : f
      ));
      
      throw error;
    }
  };

  // Direct upload for smaller files
  const uploadDirect = async (
    uploadFile: UploadFile, 
    uploadUrl: string, 
    abortController: AbortController
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadFiles(prev => prev.map(f => 
            f.id === uploadFile.id ? { ...f, progress } : f
          ));
        }
      });

      // Handle completion
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      // Handle abort
      xhr.addEventListener('abort', () => {
        reject(new Error('Upload cancelled'));
      });

      // Set up abort handling
      abortController.signal.addEventListener('abort', () => {
        xhr.abort();
      });

      // Start upload
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', uploadFile.file.type);
      xhr.send(uploadFile.file);
    });
  };

  // Multipart upload for larger files
  const uploadMultipart = async (
    uploadFile: UploadFile, 
    uploadUrl: string, 
    abortController: AbortController
  ): Promise<void> => {
    // For now, implement a simplified multipart upload
    // In a production environment, you'd want to use AWS SDK's multipart upload
    const chunkSize = 10 * 1024 * 1024; // 10MB chunks
    const totalChunks = Math.ceil(uploadFile.file.size / chunkSize);
    let uploadedBytes = 0;

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      if (abortController.signal.aborted) {
        throw new Error('Upload cancelled');
      }

      const start = chunkIndex * chunkSize;
      const end = Math.min(start + chunkSize, uploadFile.file.size);
      const chunk = uploadFile.file.slice(start, end);

      // Upload chunk (simplified - in reality you'd need to handle multipart upload properly)
      await uploadChunk(chunk, uploadUrl, abortController);
      
      uploadedBytes += chunk.size;
      const progress = Math.round((uploadedBytes / uploadFile.file.size) * 100);
      
      setUploadFiles(prev => prev.map(f => 
        f.id === uploadFile.id ? { ...f, progress } : f
      ));
    }
  };

  // Upload a single chunk
  const uploadChunk = async (
    chunk: Blob, 
    uploadUrl: string, 
    abortController: AbortController
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Chunk upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during chunk upload'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload cancelled'));
      });

      abortController.signal.addEventListener('abort', () => {
        xhr.abort();
      });

      xhr.open('PUT', uploadUrl);
      xhr.send(chunk);
    });
  };

  // Start upload process
  const startUpload = async () => {
    const pendingFiles = uploadFiles.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) return;

    setIsUploading(true);

    try {
      // Upload files sequentially to avoid overwhelming the server
      for (const uploadFile of pendingFiles) {
        await uploadSingleFile(uploadFile);
      }

      // Refresh file list
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FILES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FOLDERS] });
      
      onUploadComplete?.();
    } catch (error) {
      console.error('Upload process failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  // Retry failed upload
  const retryUpload = (uploadFile: UploadFile) => {
    setUploadFiles(prev => prev.map(f => 
      f.id === uploadFile.id 
        ? { ...f, status: 'pending', error: undefined, progress: 0 }
        : f
    ));
  };

  // Cancel upload
  const cancelUpload = (uploadFile: UploadFile) => {
    if (uploadFile.abortController) {
      uploadFile.abortController.abort();
    }
    
    setUploadFiles(prev => prev.filter(f => f.id !== uploadFile.id));
  };

  // Remove completed/error files
  const removeFile = (uploadFile: UploadFile) => {
    setUploadFiles(prev => prev.filter(f => f.id !== uploadFile.id));
  };

  // Clear all files
  const clearAll = () => {
    // Cancel any ongoing uploads
    uploadFiles.forEach(f => {
      if (f.abortController) {
        f.abortController.abort();
      }
    });
    
    setUploadFiles([]);
    setIsUploading(false);
  };

  const hasFiles = uploadFiles.length > 0;
  const pendingFiles = uploadFiles.filter(f => f.status === 'pending');
  const completedFiles = uploadFiles.filter(f => f.status === 'completed');
  const errorFiles = uploadFiles.filter(f => f.status === 'error');

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Upload className="w-5 h-5 text-blue-600" />
            <h3 className="font-medium text-gray-900">File Upload</h3>
          </div>
          
          {hasFiles && (
            <button
              onClick={clearAll}
              disabled={isUploading}
              className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
            >
              Clear All
            </button>
          )}
        </div>

        {/* Selected folder info */}
        {selectedFolder ? (
          <div className="mt-2 flex items-center space-x-2 text-sm text-gray-600">
            <FolderOpen className="w-4 h-4" />
            <span>Uploading to: {selectedFolder.concertName}</span>
          </div>
        ) : (
          <div className="mt-2 text-sm text-amber-600">
            Please select a folder before uploading files
          </div>
        )}
      </div>

      {/* Drop zone */}
      <div className="p-4">
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            ${isDragActive 
              ? 'border-blue-400 bg-blue-50' 
              : 'border-gray-300 hover:border-gray-400'
            }
            ${!selectedFolder || isUploading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <input {...getInputProps()} />
          
          <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          
          {isDragActive ? (
            <p className="text-blue-600 font-medium">Drop files here...</p>
          ) : (
            <div>
              <p className="text-gray-600 font-medium mb-2">
                Drag & drop audio files here, or click to select
              </p>
              <p className="text-sm text-gray-500">
                Supports: WAV, MP3, FLAC, AAC, OGG, M4A
              </p>
              <p className="text-sm text-gray-500">
                Maximum file size: {formatFileSize(MAX_FILE_SIZE)}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* File list */}
      {hasFiles && (
        <div className="border-t border-gray-200">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-gray-900">
                Files ({uploadFiles.length})
              </h4>
              
              {pendingFiles.length > 0 && (
                <button
                  onClick={startUpload}
                  disabled={isUploading || !selectedFolder}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  <span>
                    {isUploading ? 'Uploading...' : `Upload ${pendingFiles.length} files`}
                  </span>
                </button>
              )}
            </div>

            {/* Upload summary */}
            {(completedFiles.length > 0 || errorFiles.length > 0) && (
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-4">
                    {completedFiles.length > 0 && (
                      <span className="text-green-600">
                        ✓ {completedFiles.length} completed
                      </span>
                    )}
                    {errorFiles.length > 0 && (
                      <span className="text-red-600">
                        ✗ {errorFiles.length} failed
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* File items */}
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {uploadFiles.map((uploadFile) => (
                <FileUploadItem
                  key={uploadFile.id}
                  uploadFile={uploadFile}
                  onRetry={() => retryUpload(uploadFile)}
                  onCancel={() => cancelUpload(uploadFile)}
                  onRemove={() => removeFile(uploadFile)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Individual file upload item component
interface FileUploadItemProps {
  uploadFile: UploadFile;
  onRetry: () => void;
  onCancel: () => void;
  onRemove: () => void;
}

const FileUploadItem: React.FC<FileUploadItemProps> = ({
  uploadFile,
  onRetry,
  onCancel,
  onRemove
}) => {
  const getStatusIcon = () => {
    switch (uploadFile.status) {
      case 'pending':
        return <File className="w-4 h-4 text-gray-400" />;
      case 'uploading':
        return <Loader2 className="w-4 h-4 animate-spin text-blue-600" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <File className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = () => {
    switch (uploadFile.status) {
      case 'uploading':
        return 'bg-blue-600';
      case 'completed':
        return 'bg-green-600';
      case 'error':
        return 'bg-red-600';
      default:
        return 'bg-gray-300';
    }
  };

  return (
    <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-md">
      {/* Status icon */}
      <div className="flex-shrink-0">
        {getStatusIcon()}
      </div>

      {/* File info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-900 truncate">
            {uploadFile.file.name}
          </p>
          <span className="text-sm text-gray-500">
            {formatFileSize(uploadFile.file.size)}
          </span>
        </div>

        {/* Progress bar */}
        {uploadFile.status === 'uploading' && (
          <div className="mt-2">
            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
              <span>Uploading...</span>
              <span>{uploadFile.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${getStatusColor()}`}
                style={{ width: `${uploadFile.progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Error message */}
        {uploadFile.status === 'error' && uploadFile.error && (
          <p className="mt-1 text-sm text-red-600">
            {uploadFile.error}
          </p>
        )}

        {/* Success message */}
        {uploadFile.status === 'completed' && (
          <p className="mt-1 text-sm text-green-600">
            Upload completed successfully
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex-shrink-0 flex items-center space-x-2">
        {uploadFile.status === 'error' && (
          <button
            onClick={onRetry}
            className="p-1 text-gray-400 hover:text-blue-600"
            title="Retry upload"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
        
        {uploadFile.status === 'uploading' ? (
          <button
            onClick={onCancel}
            className="p-1 text-gray-400 hover:text-red-600"
            title="Cancel upload"
          >
            <X className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={onRemove}
            className="p-1 text-gray-400 hover:text-red-600"
            title="Remove file"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};